class VariantSelects extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('change', this.#onVariantChange);
    this.unavailableOptionValues = [];
    this.updateSelectedOptions();
    this.lastValidSelectedOptions = this.selectedOptions;
    this.isValidSelection = true;
    this.cartItems = this.closest('cart-drawer-items') || this.closest('cart-items');
    this.productInfo = this.closest('product-info');
    const variantJson = this.querySelector('[type="application/json"][data-all-variants]').textContent;
    const parsedData = JSON.parse(variantJson);
    // Ensure variantData is always an array
    this.variantData = Array.isArray(parsedData) ? parsedData : [parsedData];
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectedOptions() {
    this.selectedOptions = Array.from(this.querySelectorAll('select'), (select) => select.value);
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  #onVariantChange() {
    // Update state
    this.updateSelectedOptions();
    this.#updateCurrentVariant();

    this.#updatePickupAvailability();
    this.#removeErrorMessage();
    this.#updateVariantStatuses();

    if (!this.currentVariant) {
      if (this.productInfo) {
        this.productInfo.setUnavailable();
      }
      return;
    }

    if (this.cartItems) {
      this.#validateOptions();
      this.#updateCart();
    } else {
      this.#updateProductInfo();
    }
  }

  #updateProductInfo() {
    if (!this.productInfo) {
      return;
    }

    this.#updateURL();
    this.#updateVariantInput();

    publish(PUB_SUB_EVENTS.variantChange, {
      data: {
        sectionId: this.dataset.section,
        variant: this.currentVariant,
      },
    });
  }

  #updateVariantInput() {
    const productForms = this.productInfo.querySelectorAll(
      `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`,
    );
    productForms.forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]');
      input.value = this.currentVariant.id;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  #updateURL() {
    if (!this.currentVariant || this.dataset.updateurl === 'false') return;
    if (window.location.pathname === window.routes.cart_url) return;

    window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  #updateCart() {
    if (!this.isValidSelection || JSON.stringify(this.selectedOptions) === JSON.stringify(this.lastValidSelectedOptions)) {
      return; // Skip if invalid or unchanged
    }

    this.lastValidSelectedOptions = this.selectedOptions;

    const lineNumber = this.closest('.cart-item').dataset.index;

    this.cartItems.swapVariant(lineNumber, this.currentVariant.id);
  }

  #validateOptions() {
    this.isValidSelection = true;
    this.selectedOptions.forEach((option, index) => {
      if (this.unavailableOptionValues[index].includes(option)) {
        this.isValidSelection = false;
      }
    });

    if (!this.isValidSelection) {
      this.#updateCartLineItemError(window.variantStrings.variant_sold_out_or_unavailable);
    } else {
      this.#updateCartLineItemError(false);
    }
  }

  #updateCurrentVariant() {     
    this.currentVariant = this.variantData.find((variant) => {
      return variant.options.every((option, index) => this.selectedOptions[index] === option);
    });
  }

  #updateVariantStatuses() {
    const selectedOptionOneVariants = this.variantData.filter(
      (variant) => this.querySelector(':checked').value === variant.option1,
    );
    const inputWrappers = [...this.querySelectorAll('.product-form__input')];
    const unavailableOptionsValues = [];
    inputWrappers.forEach((option, index) => {
      if (index === 0) {
        unavailableOptionsValues.push([]);
        return;
      }
      const optionInputs = [...option.querySelectorAll('input[type="radio"], option')];
      const previousOptionSelected = inputWrappers[index - 1].querySelector(':checked').value;
      const availableOptionInputsValue = selectedOptionOneVariants
        .filter((variant) => variant.available && variant[`option${index}`] === previousOptionSelected)
        .map((variantOption) => variantOption[`option${index + 1}`]);
      const unavailableValues = this.#setInputAvailability(optionInputs, availableOptionInputsValue);
      unavailableOptionsValues.push(unavailableValues);
    });
    this.unavailableOptionValues = unavailableOptionsValues;
  }

  #setInputAvailability(listOfOptions, listOfAvailableOptions) {
    const unavailableValues = [];
    listOfOptions.forEach((input) => {
      const value = input.getAttribute('value');
      if (listOfAvailableOptions.includes(value)) {
        input.innerText = value;
      } else {
        unavailableValues.push(value);
        input.innerText = window.variantStrings.unavailable_with_option.replace('[value]', value);
      }
    });
    return unavailableValues;
  }

  #updateCartLineItemError(message) {
    const formId = this.closest('form').id;
    const submitButtons = document.querySelectorAll(`button[type=submit][form=${formId}`);
    submitButtons.forEach((btn) => btn.disabled = !!message);

    const lineItemError = this.closest('.cart-item').querySelector('.cart-item__error');
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').innerHTML = message || '';
  }

  #updatePickupAvailability() {
    const pickUpAvailability = document.querySelector('pickup-availability');
    if (!pickUpAvailability) return;

    if (this.currentVariant && this.currentVariant.available) {
      pickUpAvailability.fetchAvailability(this.currentVariant.id);
    } else {
      pickUpAvailability.removeAttribute('available');
      pickUpAvailability.innerHTML = '';
    }
  }

  #removeErrorMessage() {
    const section = this.closest('section');
    if (!section) return;

    const productForm = section.querySelector('product-form');
    if (productForm) productForm.handleErrorMessage();
  }
}

customElements.define('variant-selects', VariantSelects);

class VariantRadios extends VariantSelects {
  constructor() {
    super();
  }

  updateSelectedOptions() {
    const fieldsets = Array.from(this.querySelectorAll('fieldset'));
    this.selectedOptions = fieldsets.map((fieldset) => {
      return Array.from(fieldset.querySelectorAll('input')).find((radio) => radio.checked).value;
    });
  }
}

customElements.define('variant-radios', VariantRadios);